import { useState, useCallback } from 'react';

export interface ConfirmationOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'info' | 'warning' | 'error' | 'success';
  confirmButtonColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
}

export interface AlertOptions {
  title: string;
  message: string;
  confirmText?: string;
  variant?: 'info' | 'warning' | 'error' | 'success';
}

export interface ConfirmationState {
  open: boolean;
  options: ConfirmationOptions;
  onConfirm: () => void;
}

export interface AlertState {
  open: boolean;
  options: AlertOptions;
}

export interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  autoHideDuration?: number;
}

export const useConfirmation = () => {
  const [confirmationState, setConfirmationState] = useState<ConfirmationState>({
    open: false,
    options: {
      title: '',
      message: '',
    },
    onConfirm: () => {},
  });

  const [alertState, setAlertState] = useState<AlertState>({
    open: false,
    options: {
      title: '',
      message: '',
    },
  });

  const [snackbarState, setSnackbarState] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info',
  });

  const showConfirmation = useCallback((options: ConfirmationOptions, onConfirm: () => void) => {
    setConfirmationState({
      open: true,
      options,
      onConfirm,
    });
  }, []);

  const showAlert = useCallback((options: AlertOptions) => {
    setAlertState({
      open: true,
      options,
    });
  }, []);

  const closeConfirmation = useCallback(() => {
    setConfirmationState(prev => ({ ...prev, open: false }));
  }, []);

  const closeAlert = useCallback(() => {
    setAlertState(prev => ({ ...prev, open: false }));
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbarState(prev => ({ ...prev, open: false }));
  }, []);

  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' | 'warning' | 'info', title?: string, autoHideDuration?: number) => {
    setSnackbarState({
      open: true,
      message,
      severity,
      title,
      autoHideDuration,
    });
  }, []);

  const handleConfirm = useCallback(() => {
    confirmationState.onConfirm();
    closeConfirmation();
  }, [confirmationState.onConfirm, closeConfirmation]);

  // Enhanced versions with better UX
  const confirmDelete = useCallback((itemName: string, onConfirm: () => void) => {
    showConfirmation({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete ${itemName}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'error',
      confirmButtonColor: 'error',
    }, onConfirm);
  }, [showConfirmation]);

  const confirmAction = useCallback((actionName: string, itemName: string, onConfirm: () => void) => {
    showConfirmation({
      title: `Confirm ${actionName}`,
      message: `Are you sure you want to ${actionName.toLowerCase()} ${itemName}?`,
      confirmText: actionName,
      cancelText: 'Cancel',
      variant: 'warning',
      confirmButtonColor: 'primary',
    }, onConfirm);
  }, [showConfirmation]);

  const showValidationAlert = useCallback((errors: string[]) => {
    showAlert({
      title: 'Validation Error',
      message: `Please fix the following errors:\n\n${errors.join('\n')}`,
      variant: 'error',
      confirmText: 'OK',
    });
  }, [showAlert]);

  const showSuccessAlert = useCallback((message: string) => {
    showAlert({
      title: 'Success',
      message,
      variant: 'success',
      confirmText: 'OK',
    });
  }, [showAlert]);

  const showErrorAlert = useCallback((message: string) => {
    showAlert({
      title: 'Error',
      message,
      variant: 'error',
      confirmText: 'OK',
    });
  }, [showAlert]);

  const showInfoAlert = useCallback((message: string) => {
    showAlert({
      title: 'Information',
      message,
      variant: 'info',
      confirmText: 'OK',
    });
  }, [showAlert]);

  // Enhanced snackbar convenience functions
  const showSuccessSnackbar = useCallback((message: string, title?: string) => {
    showSnackbar(message, 'success', title);
  }, [showSnackbar]);

  const showErrorSnackbar = useCallback((message: string, title?: string) => {
    showSnackbar(message, 'error', title);
  }, [showSnackbar]);

  const showWarningSnackbar = useCallback((message: string, title?: string) => {
    showSnackbar(message, 'warning', title);
  }, [showSnackbar]);

  const showInfoSnackbar = useCallback((message: string, title?: string) => {
    showSnackbar(message, 'info', title);
  }, [showSnackbar]);

  // Utility function to handle GraphQL mutation responses
  const handleMutationResponse = useCallback((result: any, successMessage: string, mutationName: string) => {
    if (result.data?.[mutationName]?.ok) {
      showSuccessSnackbar(successMessage);
      return true;
    } else {
      const errors = result.data?.[mutationName]?.errors || ['Unknown error occurred'];
      showErrorSnackbar(errors.join(', '));
      return false;
    }
  }, [showSuccessSnackbar, showErrorSnackbar]);

  return {
    // State for rendering modals and snackbars
    confirmationState,
    alertState,
    snackbarState,
    
    // Basic functions
    showConfirmation,
    showAlert,
    closeConfirmation,
    closeAlert,
    handleConfirm,
    
    // Enhanced convenience functions
    confirmDelete,
    confirmAction,
    showValidationAlert,
    showSuccessAlert,
    showErrorAlert,
    showInfoAlert,

    // Snackbar functions
    showSnackbar,
    closeSnackbar,
    showSuccessSnackbar,
    showErrorSnackbar,
    showWarningSnackbar,
    showInfoSnackbar,
    
    // Mutation response handler
    handleMutationResponse,
  };
};

export default useConfirmation;
